import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateOrganizationSchema, generateBreadcrumbSchema } from '../../utils/seo';
import { useMemo } from 'react';
import i18n from '../../i18n';

export default function ContactPage() {
  const { t } = useTranslation();
  const isRTL = i18n.language === 'fa';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const contactSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/contact#webpage`,
        url: `${siteUrl}/contact`,
        name: 'Contact Us - Loveish Group',
        description: 'Get in touch with Loveish Group. We\'re here to answer your questions and help with your needs.',
        isPartOf: {
          '@id': `${siteUrl}/#website`
        },
        breadcrumb: {
          '@id': `${siteUrl}/contact#breadcrumb`
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/contact#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: siteUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Contact',
            item: `${siteUrl}/contact`
          }
        ]
      },
      {
        '@type': 'ContactPage',
        '@id': `${siteUrl}/contact#contactpage`,
        url: `${siteUrl}/contact`,
        name: 'Contact Loveish Group',
        description: 'Reach out to us for inquiries, support, or feedback.'
      }
    ]
  };

  useSEO({
    title: 'Contact Us - Loveish Group',
    description: 'Get in touch with Loveish Group. We\'re here to answer your questions and help with your needs.',
    keywords: 'contact Loveish Group, customer support, get in touch',
    canonical: `${siteUrl}/contact`,
    schema: contactSchema
  });

  // Falling trust symbols animation
  const fallingSymbols = useMemo(() => {
    const symbols = ['🏆', '⭐', '✅', '🎖️', '💎', '🛡️', '👍', '🤝', '💯', '✨', '🌟', '🏅'];
    const isMobile = window.innerWidth < 768;
    const symbolCount = isMobile ? 6 : 12;
    
    return Array.from({ length: symbolCount }, (_, i) => ({
      emoji: symbols[i % symbols.length],
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 5}s`,
      duration: `${8 + Math.random() * 8}s`,
      size: isMobile ? `${20 + Math.random() * 20}px` : `${30 + Math.random() * 30}px`
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formDataObj = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5cdrk7dcu97hjrn4000', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataObj as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Animated Trust Symbols */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-10">
        {fallingSymbols.map((item, index) => (
          <div
            key={index}
            className="absolute animate-fall-fruit"
            style={{
              left: item.left,
              fontSize: item.size,
              animationDuration: item.duration,
              animationDelay: item.delay,
              willChange: 'transform, opacity',
              transform: 'translate3d(0, 0, 0)',
            } as React.CSSProperties}
          >
            {item.emoji}
          </div>
        ))}
      </div>

      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 lg:pb-20 bg-gradient-to-r from-emerald-600 to-emerald-700">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            {t('contactTitle')}
          </h1>
          <p className={`text-base md:text-lg lg:text-xl text-emerald-100 max-w-2xl mx-auto leading-relaxed px-4 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('contactDescription')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Information */}
            <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 md:mb-8">
                Contact Information
              </h2>
              
              <div className="space-y-5 md:space-y-6">
                <div className={`flex items-start ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  <div className={`w-10 h-10 md:w-12 md:h-12 bg-emerald-100 rounded-full flex items-center justify-center ${isRTL ? 'ml-3 md:ml-4' : 'mr-3 md:mr-4'} flex-shrink-0`}>
                    <i className="ri-map-pin-line text-emerald-600 text-lg md:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Address</h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                      Sundukyan street/13<br />
                      Yerevan
                    </p>
                  </div>
                </div>

                <div className={`flex items-start ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  <div className={`w-10 h-10 md:w-12 md:h-12 bg-emerald-100 rounded-full flex items-center justify-center ${isRTL ? 'ml-3 md:ml-4' : 'mr-3 md:mr-4'} flex-shrink-0`}>
                    <i className="ri-phone-line text-emerald-600 text-lg md:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Phone</h3>
                    <p className="text-sm md:text-base text-gray-600">+374 93 573467</p>
                    <p className="text-sm md:text-base text-gray-600">+98 917 387 8620</p>
                  </div>
                </div>

                <div className={`flex items-start ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  <div className={`w-10 h-10 md:w-12 md:h-12 bg-emerald-100 rounded-full flex items-center justify-center ${isRTL ? 'ml-3 md:ml-4' : 'mr-3 md:mr-4'} flex-shrink-0`}>
                    <i className="ri-mail-line text-emerald-600 text-lg md:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Email</h3>
                    <p className="text-sm md:text-base text-gray-600">loveishgroup@gmail.com</p>
                  </div>
                </div>

                <div className={`flex items-start ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  <div className={`w-10 h-10 md:w-12 md:h-12 bg-emerald-100 rounded-full flex items-center justify-center ${isRTL ? 'ml-3 md:ml-4' : 'mr-3 md:mr-4'} flex-shrink-0`}>
                    <i className="ri-time-line text-emerald-600 text-lg md:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Business Hours</h3>
                    <p className="text-sm md:text-base text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM</p>
                    <p className="text-sm md:text-base text-gray-600">Saturday: 10:00 AM - 4:00 PM</p>
                    <p className="text-sm md:text-base text-gray-600">Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-8 md:mt-12">
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 md:mb-6">{t('followUs')}</h3>
                <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'} gap-3 md:gap-4`}>
                  <a href="#" className="w-10 h-10 md:w-12 md:h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-lg md:text-xl"></i>
                  </a>
                  <a href="#" className="w-10 h-10 md:w-12 md:h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-lg md:text-xl"></i>
                  </a>
                  <a href="#" className="w-10 h-10 md:w-12 md:h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-twitter-line text-lg md:text-xl"></i>
                  </a>
                  <a href="#" className="w-10 h-10 md:w-12 md:h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-telegram-line text-lg md:text-xl"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8">
              <h2 className={`text-2xl sm:text-3xl font-bold text-gray-900 mb-6 md:mb-8 ${isRTL ? 'text-right' : 'text-left'}`}>
                Send us a Message
              </h2>
              
              <form className="space-y-5 md:space-y-6" data-readdy-form id="contact-form" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      className={`w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                      placeholder="Your first name"
                      required
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      className={`w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                      placeholder="Your last name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    className={`w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    className={`w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder="What is this about?"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows={6}
                    maxLength={500}
                    className={`w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm resize-none ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder="Tell us more about your inquiry..."
                    required
                  ></textarea>
                  <p className={`text-xs text-gray-500 mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    Maximum 500 characters
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <i className="ri-check-line text-xl"></i>
                    <span>Message sent successfully! We'll get back to you soon.</span>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <i className="ri-error-warning-line text-xl"></i>
                    <span>Failed to send message. Please try again.</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 md:py-4 px-6 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className={`text-2xl sm:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center ${isRTL ? 'text-right' : 'text-left'}`}>
            Find Us
          </h2>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3047.8!2d44.5152!3d40.1872!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x406abd3b6b3b3b3b%3A0x1234567890abcdef!2sSundukyan%20St%2013%2C%20Yerevan%2C%20Armenia!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Loveish Location - Yerevan, Armenia"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}